import { useNode } from "@craftjs/core";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApplyGlobalSettings, {
  CODEBLOCK_TYPES,
} from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { updateImageStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { ASSETS } from "../../../../utils/config";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ImageBlock from "../../Shared/ImageBlock";
import useUploadSkeletonVisible from "../../Shared/useUploadSkeletonVisible";
import { getImageCarryForwardPatch } from "./carryForwardProps";
import ImageEditorSettings from "./ImageEditorSettings";
// RESPONSIVE PERF (flag REACT_APP_RESPONSIVE_PERF, default OFF): lazy-load +
// async-decode segment CONTENT images so off-screen images inside an open segment
// don't block first paint / the scroll container becoming interactive on the
// mobile responsive share view. `loading`/`decoding` pass straight through
// ImageBlock's {...rest} onto its <img>. Flag OFF -> no attribute -> byte-identical.
import { RESPONSIVE_PERF_ENABLED } from "../../../SegmentView/responsivePerfFlag";

export const Image = ({
  src,
  alt,
  width,
  isApplyMarginOnAllSides,
  defaultImage,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  showImageTitle,
  title,
  hyperlink,
  showHyperlink,
  dropShadowColor,
  alignment,
  image,
  isLoading,
  captionOverlay,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();
  const showUploadSkeleton = useUploadSkeletonVisible(isLoading);

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { getPaletteColor, getSegmentColor } = usePaletteColor();
  const { image: imageProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  // Initialize with default props from reducer for new components.
  // WEB-034: hyperlink/showHyperlink are deliberately excluded from what
  // carries forward — see carryForwardProps.js for the field list + why.
  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        Object.assign(props, getImageCarryForwardPatch(props, imageProps));
      });
    }, 5);

    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.Image);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: alignment,
            padding: marginStyle,
          }}
        >
          <ImageBlock
            src={image.src || src || defaultImage}
            hasImage={Boolean(image.src || src)}
            title={image.title || title}
            showImageTitle={showImageTitle}
            showHyperlink={showHyperlink}
            hyperlink={hyperlink}
            isImageLoading={showUploadSkeleton}
            captionOverlay={captionOverlay}
            captionBorderColor={borderColor ? getPaletteColor(borderColor) : undefined}
            captionSegmentColor={getSegmentColor()}
            {...(RESPONSIVE_PERF_ENABLED
              ? { loading: "lazy", decoding: "async" }
              : {})}
            style={{
              boxShadow:
                dropShadowColor &&
                `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
              width: width,
              borderRadius,
              border,
            }}
          />
        </div>
      </BlockIndicator>
    </div>
  );
};

export const ImageDefaultProps = {
  image: {
    src: "",
    title: "",
  },
  defaultImage: ASSETS ? ASSETS.segmentEditor.UploadImage : "",
  alt: "New image",
  height: 260,
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  width: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  showImageTitle: undefined,
  hyperlink: undefined,
  borderColor: undefined,
  showHyperlink: undefined,
  dropShadowColor: undefined,
  alignment: undefined,
  src: "",
  title: "",
  isLoading: false,
  // IMAGE_CAPTION_OVERLAY (DL-037 / N40): undefined for a plain image; a structured
  // Text overlay object when set. Forward-safe: a saved overlay still renders if the
  // flag is later OFF (the renderer never checks the flag; the OFF editor just never
  // writes it).
  captionOverlay: undefined,
};

// Use simple props instead of attempting to read from store at initialization time
Image.craft = {
  props: ImageDefaultProps,
  related: {
    settings: ImageEditorSettings,
  },
  displayName: "ImageWidget",
};
